import { useParams } from "wouter";
import { Card } from "../../ui/Card";
import { PageHeader } from "./shared";

const BUCKETS = [
  { id: "placed", label: "Placed" },
  { id: "pendingApproval", label: "Pending approval" },
  { id: "received", label: "Received" },
  { id: "canceled", label: "Canceled" },
] as const;

/**
 * Orders page (honest state).
 *
 * Observed endpoints that need more work than this turn allows:
 *   - GET /services/orders/po/bulk         (returns 405 — needs POST verb)
 *   - GET /services/orders/po/summary/needapproval (returns 200 but body
 *     payload reports "status:400 Missing Servelet request parameter")
 *
 * Rather than fake an empty state, the page tells the buyer what we
 * see today and points to Aiko for the actual list (she can call
 * orders_pendingApproval through the tool dispatcher with the right
 * headers populated from the session).
 */
export function Orders() {
  const params = useParams<{ bucket?: string }>();
  const active = params.bucket ?? "placed";

  return (
    <div className="space-y-6">
      <PageHeader title="Orders" subtitle="Your purchase orders across every outlet." />
      <nav className="flex gap-1 border-b border-line">
        {BUCKETS.map((b) => (
          <a
            key={b.id}
            href={`/app/orders/${b.id}`}
            className={
              "px-4 py-2 text-sm transition border-b-2 -mb-px " +
              (active === b.id
                ? "border-nomni-green text-spinach font-medium"
                : "border-transparent text-ink-2 hover:text-ink")
            }
          >
            {b.label}
          </a>
        ))}
      </nav>

      <Card tone="warn">
        <h3 className="font-medium text-ink mb-2">Observed endpoints, not yet bound</h3>
        <p className="text-sm text-ink-2 mb-3">
          The Zeemart order endpoints for "{active}" need more header context than the proxy currently injects. Until that's wired, ask Aiko to fetch this view; she can populate the missing parameters from your session.
        </p>
        <ul className="text-xs text-ink-mute space-y-1 font-mono">
          <li>POST /services/orders/po/bulk — needs companyId + date range</li>
          <li>GET  /services/orders/po/summary/needapproval — needs servlet request params</li>
        </ul>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(
              new CustomEvent("aiko:ask", { detail: `Show me my ${active} orders this week` }),
            );
          }}
          className="inline-flex mt-3 items-center gap-1.5 rounded-md bg-nomni-green text-canvas-2 px-3 py-1.5 text-sm font-medium hover:brightness-110 transition"
        >
          ✦ Ask Aiko for this view
        </a>
      </Card>
    </div>
  );
}
